import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CircleCheckBig,
  Clock3,
  PackageCheck,
  Radar,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import DeliveryIntelligenceCard from './DeliveryIntelligenceCard';
import StoreDeliveryIntelligenceDrawer from './StoreDeliveryIntelligenceDrawer';
import { getDeliveryIntelligenceWatch } from './deliveryIntelligenceApi';
import DateSelector from '../analytics/DateSelector';

const attentionLevels = new Set(['HIGH_ATTENTION', 'REVIEW_RECOMMENDED', 'ELEVATED']);
const physicalStages = new Set(['WITH_DRIVER', 'OUT_FOR_DELIVERY', 'POSTPONED_WITH_DRIVER']);

function todayInLima() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Lima',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export default function DeliveryIntelligence({ setActiveComponent }) {
  const [date, setDate] = useState(todayInLima);
  const [payload, setPayload] = useState({ summary: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('attention');
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getDeliveryIntelligenceWatch({ date });
      setPayload(data);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          'No pudimos cargar la inteligencia de entregas. Intenta nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 4500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const tabCounts = useMemo(() => {
    const items = payload.items || [];
    return {
      attention: items.filter(item => item.is_active !== false && attentionLevels.has(item.attention?.level) && !item.suppress_from_attention).length,
      waiting: items.filter(item => item.is_active !== false && item.attention?.level === 'WAITING_RESPONSE' && !item.suppress_from_attention).length,
      driver: items.filter(item => item.is_active !== false && physicalStages.has(item.stage)).length,
      reported: items.filter(item => ['REPORTED', 'ACKNOWLEDGED'].includes(item.store_workflow?.state)).length,
      resolved: items.filter(item => item.store_workflow?.state === 'RESOLVED').length,
      all: items.length,
    };
  }, [payload.items]);

  const visibleItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (payload.items || []).filter(item => {
      if (tab === 'attention' && (item.is_active === false || !attentionLevels.has(item.attention?.level) || item.suppress_from_attention)) return false;
      if (tab === 'waiting' && (item.is_active === false || item.attention?.level !== 'WAITING_RESPONSE' || item.suppress_from_attention)) return false;
      if (tab === 'driver' && (item.is_active === false || !physicalStages.has(item.stage))) return false;
      if (tab === 'reported' && !['REPORTED', 'ACKNOWLEDGED'].includes(item.store_workflow?.state)) return false;
      if (tab === 'resolved' && item.store_workflow?.state !== 'RESOLVED') return false;
      if (!normalizedSearch) return true;
      return (
        item.delivery?.recipient_name?.toLowerCase().includes(normalizedSearch) ||
        item.delivery?.recipient_phone?.includes(normalizedSearch) ||
        String(item.delivery_point_id).includes(normalizedSearch)
      );
    });
  }, [payload.items, search, tab]);

  const summary = payload.summary || {};
  const summaryCards = [
    { label: 'Alta atención', value: summary.high_attention || 0, icon: ShieldAlert, tone: 'text-red-700 bg-red-50 border-red-200' },
    { label: 'Revisar', value: summary.review_recommended || 0, icon: AlertTriangle, tone: 'text-amber-700 bg-amber-50 border-amber-200' },
    { label: 'Esperando respuesta', value: summary.waiting_response || 0, icon: Clock3, tone: 'text-sky-700 bg-sky-50 border-sky-200' },
    { label: 'Con conductor', value: summary.with_driver_attention || 0, icon: PackageCheck, tone: 'text-[#B1336E] bg-[#B1336E]/5 border-[#B1336E]/20' },
    { label: 'Total activas', value: summary.total_active || 0, icon: Radar, tone: 'text-slate-700 bg-slate-50 border-slate-200' },
  ];
  const tabs = [
    { id: 'attention', label: 'Atención', count: tabCounts.attention },
    { id: 'waiting', label: 'Esperando', count: tabCounts.waiting },
    { id: 'driver', label: 'Con conductor', count: tabCounts.driver },
    { id: 'reported', label: 'Reportadas', count: tabCounts.reported },
    { id: 'resolved', label: 'Resueltas', count: tabCounts.resolved },
    { id: 'all', label: 'Todas', count: tabCounts.all },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#B1336E]/5">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <button type="button" onClick={() => setActiveComponent('Options')} className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 font-montserrat">
            <ArrowLeft className="h-4 w-4" /> Volver
          </button>
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 font-montserrat">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-[#B1336E] font-montserrat">
              <Radar className="h-4 w-4" /> Señales operativas
            </p>
            <h1 className="mt-2 text-2xl font-extrabold text-slate-950 font-montserrat sm:text-3xl">
              Inteligencia de Entregas
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 font-montserrat">
              Señales operativas para ayudarte a anticipar posibles cancelaciones
            </p>
          </div>
          <div className="w-full sm:w-[230px] sm:flex-none">
            <DateSelector
              selectedDate={date}
              onDateChange={setDate}
              showCalendar={showCalendar}
              onToggleCalendar={() => setShowCalendar(value => !value)}
              label="Fecha operativa"
            />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          {summaryCards.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.label} className={`rounded-2xl border p-4 ${card.tone}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold font-montserrat">{card.label}</p>
                  <Icon className="h-4 w-4" />
                </div>
                <p className="mt-2 text-2xl font-extrabold font-montserrat">{card.value}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-2 overflow-x-auto pb-1 lg:pb-0">
              {tabs.map(tabItem => (
                <button key={tabItem.id} type="button" onClick={() => setTab(tabItem.id)} className={`whitespace-nowrap rounded-full border px-3 py-2 text-xs font-extrabold uppercase tracking-wide transition-colors font-montserrat ${
                  tab === tabItem.id
                    ? 'border-[#B1336E] bg-[#B1336E] text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-[#B1336E]/30'
                }`}>
                  {tabItem.label} · {tabItem.count}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar nombre, teléfono o ID" className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/15 font-montserrat" />
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 font-montserrat">
            {error}
          </div>
        )}

        {loading && !payload.items?.length ? (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {[1, 2, 3, 4].map(value => <div key={value} className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}
          </div>
        ) : visibleItems.length > 0 ? (
          <div className="mt-6 grid items-start gap-4 lg:grid-cols-2">
            {visibleItems.map(item => (
              <DeliveryIntelligenceCard key={item.delivery_point_id} item={item} onOpen={setSelectedItem} />
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
            <Radar className="mx-auto h-12 w-12 text-slate-300" />
            <h2 className="mt-4 text-lg font-extrabold text-slate-800 font-montserrat">Sin entregas para este filtro</h2>
            <p className="mt-2 text-sm text-slate-500 font-montserrat">
              Cambia la fecha o selecciona “Todas” para revisar la operación completa.
            </p>
          </div>
        )}

        <p className="mt-8 text-center text-xs leading-5 text-slate-500 font-montserrat">
          Esta vista recomienda atención humana. No bloquea, cancela, posterga ni modifica entregas automáticamente.
        </p>
      </main>

      {selectedItem && (
        <StoreDeliveryIntelligenceDrawer
          item={selectedItem}
          date={date}
          onClose={() => setSelectedItem(null)}
          onSignalReported={async () => {
            setToast('Reporte enviado a Fast Max. Operaciones ya puede revisarlo.');
            await load();
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-5 right-4 z-[100] flex max-w-sm items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white shadow-2xl sm:right-5 font-montserrat">
          <CircleCheckBig className="h-5 w-5 shrink-0 text-emerald-400" /> {toast}
        </div>
      )}
    </div>
  );
}
